import type { Role } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import { useListRoles } from '@sunstone/api-client/hooks';
import { Badge, Button, DataTable, PillFilter } from '@sunstone/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { useListState } from '@/hooks/useListState';
import { RESOURCE_KIND_LABELS, RESOURCE_KINDS } from '@/lib/permissions';
import { roleScopeLabel } from '@/lib/roles';
import { routes } from '@/lib/routes';

const PAGE_SIZE = 100;
const SORTABLE_COLUMNS = { name: 'Name' };
const RESOURCE_KIND_FILTER_OPTIONS = RESOURCE_KINDS.map((kind) => ({
  value: kind,
  label: RESOURCE_KIND_LABELS[kind] ?? kind,
}));

/**
 * Self-contained "New role" trigger, for a caller's PageHeader `action` slot
 * (SystemRolesPage, TenantDetail's Roles tab) — computes its own visibility
 * so callers don't need to duplicate the gating logic. tenantId set: any
 * tenant member may create a custom role for their own tenant. tenantId
 * omitted: platform-scope role, tenant-less Admin only.
 */
export function RoleCreateButton({ tenantId }: { tenantId?: string }) {
  const { isPlatformAdmin } = useAuthorization();
  const navigate = useNavigate();
  const canCreate = tenantId ? true : isPlatformAdmin;
  if (!canCreate) return null;

  return (
    <Button size="sm" onClick={() => navigate(routes.newRole(tenantId))}>
      New Role
    </Button>
  );
}

interface RoleTableProps {
  tenantId?: string;
}

/**
 * Called with a tenantId: System roles (read-only) plus that tenant's own
 * Custom roles. Called with no tenantId: every tenant_id-IS-NULL row (the
 * System Roles view). Same ListRoles RPC and the same table both times.
 * Search, sort, and pagination only — creating and editing happen elsewhere
 * (RoleCreateButton on the caller, the detail page's Edit button).
 */
export function RoleTable({ tenantId }: RoleTableProps) {
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { page, search, sortBy, sortDir, setPage, setSearch, setSort, getFilter, setFilter } =
    useListState(false);
  const resourceKindFilter = getFilter('resourceKind') ?? '';
  const { data, isLoading, error } = useListRoles(tenantId ?? '', token, {
    page,
    pageSize: PAGE_SIZE,
    search,
    sortBy: sortBy === 'name' ? 'name' : 'created_at',
    sortDir: sortBy === 'name' ? sortDir : 'desc',
    resourceKindFilter,
  });
  const roles = data?.roles ?? [];
  const totalCount = data?.totalCount ?? 0;

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        Failed to load roles.
      </div>
    );
  }

  if (!isLoading && roles.length === 0 && !search && !resourceKindFilter) {
    return (
      <EmptyState
        icon={<ShieldCheck className="w-8 h-8 text-muted-foreground/50" />}
        title="No roles"
        description="Roles determine what a member can do."
      />
    );
  }

  const columns: ColumnDef<Role>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-foreground">{row.original.name}</span>
          <Badge
            variant="outline"
            className="text-[10px]"
            title={
              row.original.grantsAll ? 'Auto-updates with new permissions' : 'Fixed permission list'
            }
          >
            {row.original.grantsAll ? 'Always current' : 'Fixed list'}
          </Badge>
        </div>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.original.description || '—'}</span>
      ),
    },
    {
      id: 'scope',
      header: 'Scope',
      cell: ({ row }) => <Badge variant="secondary">{roleScopeLabel(row.original)}</Badge>,
    },
    {
      id: 'permissions',
      header: 'Permissions',
      cell: ({ row }) =>
        row.original.grantsAll ? (
          <span className="text-muted-foreground">All</span>
        ) : (
          <span className="text-muted-foreground">{row.original.permissions.length}</span>
        ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={roles}
      showSearch
      searchPlaceholder="Search roles"
      searchValue={search}
      onSearchChange={setSearch}
      filterSlot={
        <>
          <PillFilter
            label="Resource"
            value={resourceKindFilter || null}
            onChange={(v) => setFilter('resourceKind', v)}
            options={RESOURCE_KIND_FILTER_OPTIONS}
          />
        </>
      }
      isLoading={isLoading}
      totalCount={totalCount}
      currentPage={page}
      onPageChange={setPage}
      pageSize={PAGE_SIZE}
      onRowClick={(role) => navigate(routes.roleDetail(role.id, tenantId))}
      manualSort={{ sortBy, sortDir, onSortChange: setSort, sortableColumns: SORTABLE_COLUMNS }}
    />
  );
}
